import { useEffect, useState } from 'react';
import type { Store as StoreType } from '../../../services/authService';
import storeService from '../../../services/storeService';
import type { StoreListQuery } from '../../../types/store';

type UseStoreSwitcherOptions = {
  enabled?: boolean;
  search?: string | null;
  subscriptionStatus?: string | null;
};

export const useStoreSwitcher = (options: boolean | UseStoreSwitcherOptions = true) => {
  const enabled = typeof options === 'boolean' ? options : options.enabled ?? true;
  const rawSearch = typeof options === 'boolean' ? null : options.search;
  const rawSubscriptionStatus = typeof options === 'boolean' ? null : options.subscriptionStatus;
  const search = (rawSearch || '').trim();
  const subscriptionStatus = rawSubscriptionStatus && rawSubscriptionStatus !== 'all'
    ? rawSubscriptionStatus
    : null;
  const [stores, setStores] = useState<StoreType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!enabled) {
      setStores([]);
      setLoading(false);
      setError('');
      return undefined;
    }

    let isCurrent = true;

    const loadStores = async () => {
      setLoading(true);
      setError('');

      try {
        const query: StoreListQuery = { get_all: true };
        if (subscriptionStatus) {
          query.subscription_status = subscriptionStatus;
        }
        if (search) {
          query.search = search;
        }

        const response = await storeService.getStores(query);

        if (!isCurrent) {
          return;
        }

        setStores(Array.isArray(response.stores) ? response.stores : []);
      } catch (requestError: any) {
        if (!isCurrent) {
          return;
        }

        setError(requestError?.message || 'Failed to load stores');
        setStores([]);
      } finally {
        if (isCurrent) {
          setLoading(false);
        }
      }
    };

    void loadStores();

    return () => {
      isCurrent = false;
    };
  }, [enabled, search, subscriptionStatus]);

  return {
    stores,
    loading,
    error,
  };
};
